/**
*  nuxt项目目录/composables/http.ts
*/
//  基于useFetch()的网络请求封装
import { request as axiosRequest } from './http'
import { generateDeviceFlags} from './device'

let deviceInfoCache = ''
export interface RequestExtraParams {
  /**
   * 扩展请求头
   *
   * 如果增加了`gomock`字段，请求会走到YApi的mock环境
   *  */
  headers?: any
  /**
   * 请求取消方法，
   * 返回一个Promise，
   * 当这个Promise比实际请求先返回时，请求返回的是该cancel方法返回的值
   */
  cancel?: Promise<string | undefined>
  /** 请求是否需要携带clientinfo */
  includeClientInfo?: boolean
  /** request cancellation token */
  cancelToken?: symbol | string | number
  /** 请求超时时间, 默认10s，单位是ms */
  timeout?: number
  /** 是否显示loading， 如果使用tclist 分页加载数据，不推荐,会多次回调load */
  loading?: boolean
  /** 是否只返回body（默认情况为true） */
  responseOnlyBody?: boolean
}

export interface RequestWrappedParams extends RequestExtraParams {
  /** http method */
  method: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'OPTIONS' | 'PATCH' | 'HEAD'
  url: string
  /** url query */
  query?: object
  /** post json data */
  data?: any
  clientInfo?: any,
  /** 请求内容类型 */
  contentType?: ContentType
}

/** 获取promise函数返回 */
export type ReturnPromise<T> = T extends (...args: any[]) => Promise<infer R>
  ? NonNullable<R>
  : never

/** 获取函数参数 */
export type ParamData<T> = T extends (...args: [infer P, ...any[]]) => any
  ? P
  : never

export enum ContentType {
  Json = 'application/json',
  FormData = 'multipart/form-data',
  UrlEncoded = 'application/x-www-form-urlencoded',
  Text = 'text/plain',
}

/** 请求错误的返回内容 */
export interface RequestFailResponse<T = unknown> {
  /** 对应的状态码 */
  status: number
  /** 对应的错误信息 */
  message: string
  /** 异常请求的完整response */
  reason: T
  /** 请求的trace id */
  id?: string
  /** 请求地址 */
  __url?: string
}

/** 不再兼容非标准的数据结构 */
export declare type AjaxPromise<R, E = any> = Promise<
  R extends {
    status?: number
    message?: string
    body?: infer Body
  }
  ?
  | (Body & {
    /** 服务器响应的时间戳 */
    _timestamp?: string
    /** 请求的id */
    _traceid?: string
    /** 最外层的状态码 */
    status?: number
    /** 最外层的消息 */
    message?: string
    /** 最外层的消息 */
    body?: Body
  })
  | null
  : R
>

function generateClientInfo() {
  if(deviceInfoCache) return deviceInfoCache
  const {deviceId,appVersion} = generateDeviceFlags()
  const deviceInfo = {
    deviceId,
    appVersion,
    platform: "PC",
  };
  deviceInfoCache = JSON.stringify(deviceInfo)
  return deviceInfoCache;
}


/**
 * 发起请求方法
 * @param param0 请求参数 {@link RequestWrappedParams}
 * @param _basePath 请求基础路径，默认当前域名
 *
 * @returns Promise<any> 当请求错误时，返回 {@link RequestFailResponse}
 */
export function request<B, E>(
  params: RequestWrappedParams,
  _basePath?: string,
): AjaxPromise<B, E> {
  // params.clientInfo = 
  const clientInfo = generateClientInfo()
  const requestBody = params.data ? { ...params.data } : { }
  // 增加H5的版本号和构建时间
  params.data = requestBody
  // tips: 在hopegoo环境，token是由客户端负责携带的，H5无法干涉，注意区分。
  params.headers = Object.assign({}, params.headers)
  // 如果是开发环境且检测到gomock参数，则请求地址走mock
  params = Object.assign(params || {}, { clientInfo });
  const origion
    = typeof location !== 'undefined'
      ? location.origin
      : import.meta.env.VITE_API_URL
  const protocol
    = typeof location !== 'undefined' ? location.protocol : 'http:'
  /** 基础的请求host */
  let urlBase = import.meta.env.VITE_API_URL || origion
  urlBase = /^\/\//.test(urlBase) ? protocol + urlBase : urlBase
  // 防止避免双斜杠协议new URL的时候报错
  const requestUrl = new URL(params.url, urlBase)

  // query参数拼接
  if (params.method.toUpperCase() === 'GET' || params.query) {
    const queryBody = (params.query || {}) as Record<string, any>
    Object.keys(queryBody).forEach(key =>
      requestUrl.searchParams.append(key, queryBody[key]),
    )
    const urlString = requestUrl.toString()
    // 拼完参数后，去掉host部分，只保留path
    params.url = urlString.replace(urlBase, '')
  }

  const requestHandler = axiosRequest(params as any)

  // 赋予默认值 10s
  params.timeout = params.timeout != null ? params.timeout : 10000
  let fetchResult = requestHandler
  if (params.timeout) {
    // eslint-disable-next-line promise/param-names
    const timeout = new Promise((_: any, reject) => {
      setTimeout(() => {
        const error: RequestFailResponse = {
          status: 1000,
          message: 'The request time was exceeded',
          reason: params,
        }
        reject(error)
      }, params.timeout)
    })
    fetchResult = Promise.race([timeout, requestHandler])
  }
  // 异常处理统一增加url，方便排查
  return fetchResult.catch((error) => {
    error.__url = params.url
    return Promise.reject(error)
  })
}
